function normalizeLocale(locale: any): string {
	return (locale || '')
		.toString()
		.trim()
		.replace(/-/g, '_')
		.toLowerCase();
}

function localeVariants(locale: any): string[] {
	const normalized = normalizeLocale(locale);
	if (!normalized) return [];
	const base = normalized.split('_')[0];
	return Array.from(new Set([normalized, base].filter((x) => !!x)));
}

function localizedKey(baseKey: string, locale: string): string[] {
	const base = locale.split('_')[0];
	const variants = Array.from(
		new Set([
			`${baseKey}${base.toUpperCase()}`,
			`${baseKey}${base.charAt(0).toUpperCase() + base.slice(1)}`,
			`${baseKey}_${base}`
		].filter((x) => !!x))
	);
	return variants;
}

function firstNonEmpty(values: any[]): string {
	for (const value of values) {
		if (value != null && value !== '') return value.toString().trim();
	}
	return '';
}

function localizedTranslationValue(translation: any, baseKey: string): any {
	if (!translation || typeof translation !== 'object') return '';

	if (baseKey === 'text' || baseKey === 'label' || baseKey === 'html') {
		return (
			translation?.translationText ??
			translation?.text ??
			translation?.label ??
			translation?.html ??
			''
		);
	}
	if (baseKey === 'title' || baseKey === 'name' || baseKey === 'fullTitle') {
		return (
			translation?.translationTextName ??
			translation?.title ??
			translation?.name ??
			''
		);
	}
	if (baseKey === 'description' || baseKey === 'summary') {
		return (
			translation?.translationTextDescription ??
			translation?.description ??
			translation?.summary ??
			''
		);
	}
	if (baseKey === 'placeholder') {
		return translation?.placeholder ?? translation?.translationText ?? '';
	}
	if (baseKey === 'instructions' || baseKey === 'timeframeText') {
		return translation?.instructions ?? translation?.timeframeText ?? translation?.translationText ?? '';
	}

	return translation?.[baseKey] ?? '';
}

const TRANSLATABLE_KEYS = [
	'title',
	'name',
	'summary',
	'description',
	'instructions',
	'placeholder',
	'label',
	'text',
	'html',
	'fullTitle',
	'timeframeText',
	'reflections'
];

function localizeObject(input: any, locale: any): any {
	if (Array.isArray(input)) return input.map((item) => localizeObject(item, locale));
	if (!input || typeof input !== 'object') return input;

	const out: any = {};
	Object.keys(input).forEach((key) => {
		out[key] = localizeObject(input[key], locale);
	});

	const variants = localeVariants(locale);

	if (Array.isArray(input?.translations)) {
		for (const variant of variants) {
			const match = input.translations.find((entry: any) => normalizeLocale(entry?.language) === variant);
			if (!match) continue;
			for (const baseKey of TRANSLATABLE_KEYS) {
				const translated = localizedTranslationValue(match, baseKey);
				if (translated != null && translated !== '') {
					out[baseKey] = localizeObject(translated, locale);
				}
			}
			break;
		}
	}

	if (input?.translation && typeof input.translation === 'object') {
		const translationLanguage = normalizeLocale(input.translation.language);
		if (!translationLanguage || variants.includes(translationLanguage)) {
			for (const baseKey of TRANSLATABLE_KEYS) {
				const translated = localizedTranslationValue(input.translation, baseKey);
				if (translated != null && translated !== '') {
					out[baseKey] = localizeObject(translated, locale);
				}
			}
		}
	}

	for (const variant of variants) {
		for (const baseKey of TRANSLATABLE_KEYS) {
			for (const key of localizedKey(baseKey, variant)) {
				const value = input?.[key];
				if (value != null && value !== '') {
					out[baseKey] = localizeObject(value, locale);
					break;
				}
			}
		}
	}

	if (!out.text) {
		out.text = firstNonEmpty([input?.defaultText, input?.valueText, input?.valueLabel]);
	}
	if (!out.label) {
		out.label = firstNonEmpty([input?.defaultText, input?.valueLabel, out.text]);
	}

	return out;
}

export function localizeToolkitContent(content: any, locale: any): any {
	return localizeObject(content, locale);
}
